import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentLabelsUpdate,
  DocumentCraftingLabel,
  DocumentCraftingLabelScope,
} from "../../../Models/documentCrafting";
import {
  LabelChip,
  LabelColorDot,
  LabelScopeIcon,
} from "../Common/LabelChips";

/** Label names are unique per scope and at most 40 characters (SPEC §4.13). */
export const LABEL_NAME_MAX = 40;

interface CreateOption {
  create: true;
  name: string;
}

type PickerOption = DocumentCraftingLabel | CreateOption;
type PickerValue = PickerOption | string;

const isCreate = (option: PickerValue): option is CreateOption =>
  typeof option === "object" && "create" in option;

const isLabel = (option: PickerValue): option is DocumentCraftingLabel =>
  typeof option === "object" && "id" in option;

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** The personal label with this name (case-insensitive), if the user has one. */
export const findPersonalLabel = (
  labels: DocumentCraftingLabel[],
  name: string,
) => labels.find((label) => sameName(label.name, name));

/** A typed name that may become a new personal label ("" if not). */
export const newLabelName = (text: string) => {
  const name = text.trim();
  return name && name.length <= LABEL_NAME_MAX ? name : "";
};

const filter = createFilterOptions<PickerOption>({
  stringify: (option) => option.name,
});

interface Props {
  /** Active project labels of the workspace. */
  projectOptions: DocumentCraftingLabel[];
  /** The caller's personal labels. */
  personalOptions: DocumentCraftingLabel[];
  /** Labels on the document (archived project labels stay, greyed). */
  projectValue: DocumentCraftingLabel[];
  personalValue: DocumentCraftingLabel[];
  /** Project labels need edit rights; personal labels only view rights. */
  canEditProject: boolean;
  loading?: boolean;
  autoFocus?: boolean;
  /** Called with the ids of the scopes that changed (PUT /labels/). */
  onChange: (change: DocumentCraftingDocumentLabelsUpdate) => void;
  /** A new personal label typed by the user; the caller creates and adds it. */
  onCreatePersonal: (name: string) => void;
}

/**
 * Picks the labels of a document: project labels from the workspace's fixed
 * list (editors only) and the user's personal labels, which can also be
 * created inline by typing a new name. Project and personal labels are told
 * apart by a group and a person icon.
 */
const LabelPicker: React.FC<Props> = ({
  projectOptions,
  personalOptions,
  projectValue,
  personalValue,
  canEditProject,
  loading,
  autoFocus,
  onChange,
  onCreatePersonal,
}) => {
  const { t } = useTranslation("documentCrafting");
  const [input, setInput] = useState("");

  const options: PickerOption[] = [
    ...(canEditProject ? projectOptions : []),
    ...personalOptions,
  ];
  const value: PickerOption[] = [...projectValue, ...personalValue];
  const tooLong = input.trim().length > LABEL_NAME_MAX;

  const handleChange = (_event: React.SyntheticEvent, next: PickerValue[]) => {
    const labels = next.filter(isLabel);
    const typed = next
      .filter((option): option is string | CreateOption => !isLabel(option))
      .map((option) => (isCreate(option) ? option.name : option));

    const personalIds = labels
      .filter((label) => label.scope === DocumentCraftingLabelScope.PERSONAL)
      .map((label) => label.id);
    typed.forEach((text) => {
      const existing = findPersonalLabel(personalOptions, text);
      if (existing) {
        if (!personalIds.includes(existing.id)) personalIds.push(existing.id);
        return;
      }
      const name = newLabelName(text);
      if (name) onCreatePersonal(name);
    });
    const projectIds = labels
      .filter((label) => label.scope === DocumentCraftingLabelScope.PROJECT)
      .map((label) => label.id);

    const change: DocumentCraftingDocumentLabelsUpdate = {};
    if (
      canEditProject &&
      !sameIds(
        projectIds,
        projectValue.map((label) => label.id),
      )
    ) {
      change.project_label_ids = projectIds;
    }
    if (
      !sameIds(
        personalIds,
        personalValue.map((label) => label.id),
      )
    ) {
      change.personal_label_ids = personalIds;
    }
    if (change.project_label_ids || change.personal_label_ids) onChange(change);
    setInput("");
  };

  return (
    <Autocomplete<PickerOption, true, false, true>
      multiple
      freeSolo
      filterSelectedOptions
      selectOnFocus
      handleHomeEndKeys
      options={options}
      value={value}
      inputValue={input}
      onInputChange={(_event, text, reason) => {
        if (reason !== "reset") setInput(text);
      }}
      onChange={handleChange}
      loading={loading}
      isOptionEqualToValue={(option, selected) =>
        isLabel(option) && isLabel(selected) && option.id === selected.id
      }
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      groupBy={(option) =>
        isLabel(option) && option.scope === DocumentCraftingLabelScope.PROJECT
          ? t("detail.labels.projectGroup")
          : t("detail.labels.personalGroup")
      }
      filterOptions={(all, state) => {
        const filtered = filter(all, state);
        const name = newLabelName(state.inputValue);
        if (name && !findPersonalLabel(personalOptions, name)) {
          filtered.push({ create: true, name });
        }
        return filtered;
      }}
      noOptionsText={t("detail.labels.noOptions")}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key: React.Key;
        };
        return (
          <Box
            component="li"
            key={isCreate(option) ? `create-${option.name}` : key}
            {...optionProps}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {isCreate(option) ? (
              <>
                <AddRounded sx={{ fontSize: 18, color: color.gray500 }} />
                <Typography component="span" sx={{ fontSize: 14 }}>
                  {t("detail.labels.create", { name: option.name })}
                </Typography>
              </>
            ) : (
              <>
                <LabelColorDot value={option.color} />
                <LabelScopeIcon scope={option.scope} />
                <Typography component="span" noWrap sx={{ fontSize: 14 }}>
                  {option.name}
                </Typography>
              </>
            )}
          </Box>
        );
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, index) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });
          if (!isLabel(tag)) return null;
          const removable =
            canEditProject || tag.scope === DocumentCraftingLabelScope.PERSONAL;
          return (
            <LabelChip
              key={key}
              label={tag}
              {...tagProps}
              onDelete={removable ? onDelete : undefined}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          autoFocus={autoFocus}
          placeholder={t("detail.labels.placeholder")}
          error={tooLong}
          helperText={
            tooLong
              ? t("detail.labels.tooLong", { max: LABEL_NAME_MAX })
              : canEditProject
                ? undefined
                : t("detail.labels.projectReadOnly")
          }
          inputProps={{
            ...params.inputProps,
            "aria-label": t("detail.labels.edit"),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LabelPicker;
